import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatAction, ChatVerAction, ChatConfirmAction, ChatVerRecursoAction } from '../../types/chat';
import { executeVerAction, executeConfirmAction } from '../../services/chat/seleccionService';
import { dedupeChatActions } from '../../helpers/chatActionsHelper';
import { useChat } from '../../contexts/ChatContext';

interface ActionCardProps {
  actions: ChatAction[];
}

/**
 * ActionCard — renderiza los botones de acción que el backend adjunta al evento
 * `done` (ver / confirmar). Se monta dentro de MessageBubble cuando el mensaje
 * del asistente trae `actions`.
 */
const ActionCard: React.FC<ActionCardProps> = ({ actions }) => {
  const navigate = useNavigate();
  const { getActiveSessionId } = useChat();
  const [pendingLabel, setPendingLabel] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  // Confirmaciones ya aplicadas (por pending_op_id): el botón queda apretado y
  // deshabilitado para que nadie lo toque dos veces.
  const [aplicadas, setAplicadas] = useState<Set<string>>(new Set());

  // Defensa: el backend a veces envía acciones duplicadas en el evento `done`.
  // Colapsamos duplicados EXACTOS (mismo tipo + label + selección/op) para no
  // mostrar dos botones idénticos; nunca junta acciones distintas.
  const uniqueActions = useMemo(() => dedupeChatActions(actions), [actions]);

  const handleVer = async (a: ChatVerAction) => {
    setPendingLabel(a.label);
    setFeedback(null);
    try {
      await executeVerAction(a, navigate);
    } catch (e) {
      setFeedback(e instanceof Error ? e.message : 'No se pudo abrir.');
    } finally {
      setPendingLabel(null);
    }
  };

  const handleConfirm = async (a: ChatConfirmAction) => {
    setPendingLabel(a.label);
    setFeedback(null);
    try {
      // El sessionId viaja al backend para que el AGENTE sepa que esta op se
      // ejecutó por botón (incidente 2026-10-31): sin esto, el próximo turno
      // del chat confabulaba el estado ("no se creó nada").
      const r = await executeConfirmAction(a, getActiveSessionId() || undefined);
      const toolOk = (r.result as { success?: boolean } | null | undefined)?.success !== false;
      if (r.simulated) setFeedback('🧪 Simulado — no se guardaron cambios.');
      else if (r.success && toolOk) {
        setFeedback('✓ Operación aplicada.');
        setAplicadas((prev) => new Set(prev).add(a.pending_op_id));
      } else setFeedback('No se pudo aplicar.');
    } catch (e) {
      setFeedback(e instanceof Error ? e.message : 'No se pudo aplicar.');
    } finally {
      setPendingLabel(null);
    }
  };

  const handleVerRecurso = (a: ChatVerRecursoAction) => {
    navigate(a.target);
  };

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {uniqueActions.map((a, i) => {
        const isVer = a.tipo === 'ver';
        const isConfirmar = a.tipo === 'confirmar';
        const isRecurso = a.tipo === 'ver_recurso';
        const isLoading = pendingLabel === a.label;
        const yaAplicada = isConfirmar && aplicadas.has((a as ChatConfirmAction).pending_op_id);
        const handleClick = () => {
          if (isVer) return handleVer(a as ChatVerAction);
          if (isConfirmar) return handleConfirm(a as ChatConfirmAction);
          if (isRecurso) return handleVerRecurso(a as ChatVerRecursoAction);
        };
        const colorClass = yaAplicada
          ? 'bg-paper text-sage border border-hairline'
          : isVer
          ? 'bg-forest text-paper hover:bg-forest-mid'
          : isConfirmar
            ? 'bg-forest-mid text-paper hover:bg-forest-mid/90'
            : 'bg-info text-paper hover:bg-info/90';
        const loadingText = isVer ? 'Generando listado…' : isConfirmar ? 'Aplicando…' : 'Abriendo…';
        return (
          <button
            key={i}
            type="button"
            onClick={handleClick}
            disabled={yaAplicada || (pendingLabel !== null && !isRecurso)}
            aria-pressed={isConfirmar ? yaAplicada : undefined}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:cursor-not-allowed ${yaAplicada ? '' : 'disabled:opacity-60'} ${colorClass}`}
          >
            {yaAplicada ? (
              <span>✓ Confirmado</span>
            ) : isLoading ? (
              <span className="flex items-center gap-1.5">
                <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {loadingText}
              </span>
            ) : a.label}
          </button>
        );
      })}
      {feedback && <div className="w-full text-xs text-sage mt-1">{feedback}</div>}
    </div>
  );
};

export default ActionCard;
